import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ShieldCheck, Instagram, MapPin, Clock, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { absUrl } from "@/lib/helpers";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const NAV = [
  { to: "/", label: "Beranda", testid: "nav-beranda" },
  { to: "/katalog", label: "Produk", testid: "nav-katalog" },
  { to: "/cara-pesan", label: "Cara Pesan", testid: "nav-cara-pesan" },
  { to: "/kontak", label: "Kontak", testid: "nav-kontak" },
];

function Brand({ settings, onClick }) {
  return (
    <Link to="/" onClick={onClick} data-testid="header-logo" className="flex items-center gap-2.5">
      {settings?.logo_url ? (
        <img src={absUrl(settings.logo_url)} alt={settings.store_name} className="h-9 w-9 rounded-xl object-cover ring-1 ring-border" />
      ) : (
        <div className="h-9 w-9 rounded-xl bg-primary" />
      )}
      <span className="font-heading text-lg font-extrabold tracking-tight">{settings?.store_name || "UPPLESORIES"}</span>
    </Link>
  );
}

export default function Layout() {
  const { settings } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {settings?.announcement_text && (
        <div data-testid="announcement-bar" className="bg-accent text-accent-foreground text-center text-xs sm:text-sm py-2 px-4 font-medium">
          {settings.announcement_text}
        </div>
      )}

      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 py-3">
          <Brand settings={settings} />
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(n.to) ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground hover:bg-secondary"
                }`}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/admin/login"
              data-testid="nav-admin"
              className="ml-2 flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ShieldCheck className="h-4 w-4" /> Admin
            </Link>
          </nav>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="md:hidden rounded-xl border border-border p-2" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetTitle className="mb-6"><Brand settings={settings} onClick={() => setOpen(false)} /></SheetTitle>
              <div className="flex flex-col gap-1">
                {NAV.map((n) => (
                  <button
                    key={n.to}
                    data-testid={`m-${n.testid}`}
                    onClick={() => { navigate(n.to); setOpen(false); }}
                    className={`text-left rounded-xl px-4 py-3 font-medium transition-colors ${
                      isActive(n.to) ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
                    }`}
                  >
                    {n.label}
                  </button>
                ))}
                <button
                  onClick={() => { navigate("/admin/login"); setOpen(false); }}
                  className="mt-3 flex items-center gap-2 rounded-xl border border-border px-4 py-3 text-muted-foreground"
                >
                  <ShieldCheck className="h-4 w-4" /> Halaman Admin
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="mt-16 border-t border-border bg-primary text-primary-foreground">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3">
            <Brand settings={settings} />
            <p data-testid="footer-description" className="text-sm text-primary-foreground/70">{settings?.footer_description || settings?.seo_description || "Toko aksesoris HP premium."}</p>
          </div>
          <div className="space-y-3 text-sm">
            <h4 className="font-heading font-semibold">Kontak</h4>
            <p className="flex items-start gap-2 text-primary-foreground/70"><MapPin className="h-4 w-4 mt-0.5 shrink-0" />{settings?.address}</p>
            <p className="flex items-center gap-2 text-primary-foreground/70"><Clock className="h-4 w-4 shrink-0" />{settings?.operating_hours}</p>
            <p className="flex items-center gap-2 text-primary-foreground/70"><Phone className="h-4 w-4 shrink-0" />+{settings?.whatsapp_number}</p>
          </div>
          <div className="space-y-3 text-sm">
            <h4 className="font-heading font-semibold">Ikuti Kami</h4>
            {settings?.instagram && <a href={`https://instagram.com/${settings.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground"><Instagram className="h-4 w-4" />@{settings.instagram}</a>}
            {settings?.tiktok && <a href={`https://tiktok.com/@${settings.tiktok}`} target="_blank" rel="noreferrer" className="block text-primary-foreground/70 hover:text-primary-foreground">TikTok @{settings.tiktok}</a>}
          </div>
          <div className="space-y-3 text-sm">
            <h4 className="font-heading font-semibold">Navigasi</h4>
            {NAV.map((n) => <Link key={n.to} to={n.to} className="block text-primary-foreground/70 hover:text-primary-foreground">{n.label}</Link>)}
          </div>
        </div>
        <div data-testid="footer-copyright" className="border-t border-primary-foreground/10 py-4 text-center text-xs text-primary-foreground/60">
          {settings?.footer_copyright || `© ${new Date().getFullYear()} ${settings?.store_name || "UPPLESORIES"}. Semua hak dilindungi.`}
        </div>
      </footer>

      <FloatingWhatsApp />
    </div>
  );
}
